import React, { useState, useRef, useEffect } from "react";
import AdminTopNavBar from "../common/AdminTopNavBar";
import "../../styles/Admin.css";
import AdminFooter from "../common/AdminFooter";
import AdminSideBar from "./AdminSideBar";
import getUserMenuItems from "./AdminMenuContent";
import {
  adminReqConfig,
  api,
  baseURL,
  superAdminReqConfig,
} from "../../include/api";
import { useNotification } from "../../Toast/ToastProvider";
import { useNavigate, useSearchParams } from "react-router-dom";
import { req } from "../../include/function";
import { ERROR, SUCCESS } from "../../include/constant";

const AddSlide = () => {
  const homeLink = "/admin";
  const toast = useNotification();
  const imageElement = useRef();
  const navigate = useNavigate();
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const toggleSidebar = () => setIsSidebarMinimized(!isSidebarMinimized);
  const menuContent = getUserMenuItems();
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [view, setView] = useState("");
  const [slideType, setSlideType] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState(true);
  const [shopId, setShopId] = useState(1);
  // const [productId, setProductId] = useState(0);
  const [imageError, setImageError] = useState("");
  const [statusError, setStatusError] = useState("");
  const [slideTypeError, setSlideTypeError] = useState("");
  const [slideDescriptionError, setSlideDescriptionError] = useState("");
  const [slideTitleError, setSlideTitleError] = useState("");
  const [viewError, setViewError] = useState("");
  const [nameError, setNameError] = useState("");
  const [searchParams] = useSearchParams();
  const slideIdFromUrl = searchParams.get("id");
  const isViewMode = searchParams.get("view") === "true";
  const isEditMode = !!slideIdFromUrl && !isViewMode;
  const [categoryId, setCategoryId] = useState("");
  const [brandId, setBrandId] = useState("");
  const [productId, setProductId] = useState("");
  const [categoryList, setCategoryList] = useState([]);
  const [brandList, setBrandList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [fetchingCategory, setFetchingCategory] = useState(true);
  const [fetchingBrand, setFetchingBrand] = useState(false);
  const [fetchingProduct, setFetchingProduct] = useState(false);
  // BUTTON STYLING HELPERS
  const getButtonStyle = (isPrimary = false, isSmall = false) => ({
    border: "none",
    padding: isSmall ? "8px 12px" : "8px 20px",
    fontWeight: "500",
    borderRadius: "5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: saving ? "not-allowed" : "pointer",
    opacity: saving ? 0.7 : 1,
    fontSize: isSmall ? "14px" : "inherit",
    minWidth: isSmall ? "80px" : "auto",
    textAlign: "center",
    ...(isPrimary
      ? {
          backgroundColor: saving ? "#1565c0" : "#2B95D3",
          color: "#fff",
        }
      : {
          backgroundColor: saving ? "#e0e0e0" : "#D9EBFF",
          color: saving ? "#9e9e9e" : "#45A2DA",
        }),
  });

  const renderLoadingSpinner = (size = "sm") => (
    <i
      className={`fa fa-spinner fa-spin ${size === "sm" ? "me-2" : "me-3"}`}
      aria-hidden="true"
      style={{ fontSize: size === "sm" ? "0.9rem" : "1.1rem" }}
    ></i>
  );

  // ✅ Fetch Categories on mount
  useEffect(() => {
    setFetchingCategory(true);
    api
      .get("/category/", adminReqConfig())
      .then((res) => {
        if (res.status === 200) {
          setCategoryList(res.data.data || []);
        }
      })
      .catch(() => setCategoryList([]))
      .finally(() => setFetchingCategory(false));
  }, []);

  // ✅ Fetch Brands when Category changes (Single Select Logic)
  useEffect(() => {
    if (!categoryId) {
      setBrandList([]);
      setProductList([]);
      setBrandId("");
      setProductId("");
      return;
    }
    setFetchingBrand(true);
    api
      .get(`/brand/?categoryId=${categoryId}`, adminReqConfig())
      .then((res) => {
        if (res.status === 200) {
          // ✅ Deduplicate brands by ID
          const uniqueBrands = (res.data.data || []).filter(
            (brand, index, self) =>
              index === self.findIndex((b) => b.id === brand.id),
          );
          setBrandList(uniqueBrands);
        }
      })
      .catch(() => setBrandList([]))
      .finally(() => setFetchingBrand(false));
  }, [categoryId]);

  // ✅ Fetch Products when Brand changes
  useEffect(() => {
    if (!brandId) {
      setProductList([]);
      setProductId("");
      return;
    }

    setFetchingProduct(true);
    api
      .get(
        `/product/?categoryId=${categoryId}&brandId=${brandId}`,
        adminReqConfig(),
      )
      .then((res) => {
        if (res.status === 200) {
          const products = res.data.data || [];

          // ✅ FIX: Use Set for more reliable deduplication by ID
          const seenIds = new Set();
          const uniqueProducts = products.filter((product) => {
            if (seenIds.has(product.productId)) {
              return false; // Duplicate - filter out
            }
            seenIds.add(product.productId);
            return true;
          });

          // ✅ Also sort by name for better UX
          uniqueProducts.sort((a, b) => a.name.localeCompare(b.name));

          setProductList(uniqueProducts);
        }
      })
      .catch(() => setProductList([]))
      .finally(() => setFetchingProduct(false));
  }, [brandId]);

  const imageSizeMap = {
    main: { width: 1680, height: 499 },
    "best seller": { width: 824, height: 239 },
    "hot deals": { width: 824, height: 239 },
    "day deals": { width: 824, height: 239 },
    "deals of the day": { width: 1362, height: 277 },
    "top selling products": { width: 544, height: 608 },
    "trending products": { width: 1435, height: 282 },
    "featured products": { width: 544, height: 488 },
    "big deals slide": { width: 1680, height: 150 },
    "new arrivals": { width: 1680, height: 150 },
    "recommended for you": { width: 603, height: 820 },
    "mobile-main": { width: 1383, height: 624 },
    "mobile-best seller": { width: 2382, height: 870 },
    "mobile-hot deals": { width: 2382, height: 870 },
    "mobile-day deals": { width: 2382, height: 870 },
    "mobile-new arrivals": { width: 2382, height: 870 },
    "mobile-ad banner1": { width: 554, height: 490 },
    "mobile-ad banner2": { width: 554, height: 490 },
    thumbnail1: { width: 600, height: 600 },
    thumbnail2: { width: 600, height: 600 },
    thumbnail3: { width: 600, height: 600 },
    thumbnail4: { width: 600, height: 600 },
    sponsored: { width: 600, height: 320 },
  };

  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  const clearFields = () => {
    if (saving) return;
    setName("");
    setView("");
    setSlideType("");
    setTitle("");
    setDescription("");
    setStatus(true);
    setImage(null);
    setImagePreview("");
    setImageError("");
    setStatusError("");
    setSlideTypeError("");
    setSlideDescriptionError("");
    setSlideTitleError("");
    setViewError("");
    setNameError("");
    setCategoryId("");
    setBrandId("");
    setProductId("");
  };

  const handleImageChange = (files) => {
    if (saving || files.length === 0) {
      if (saving) setImageError("Please wait while saving...");
      return;
    }

    const selectedImage = files[0];

    // File type validation
    if (
      !["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(
        selectedImage.type,
      )
    ) {
      setImageError(
        "Unsupported file format (jpeg, jpg, png, webp are supported)",
      );
      return;
    }

    // File size validation
    if (selectedImage.size / 1024 / 1024 > 3) {
      setImageError("File is too large (max 3MB)");
      return;
    }

    // Dimension validation
    if (!slideType) {
      setImageError("Please select a slide type first");
      return;
    }

    const expected = imageSizeMap[slideType];
    if (!expected) {
      setImageError("Invalid slide type");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const { width: actualWidth, height: actualHeight } = img;
        const { width: expectedWidth, height: expectedHeight } = expected;

        // Handle "auto" width (only validate height)
        if (expectedWidth === "auto") {
          if (actualHeight !== expectedHeight) {
            setImageError(
              `Image must be exactly ${expectedWidth} x ${expectedHeight}px (height: ${expectedHeight}px required)`,
            );
            return;
          }
        } else {
          // Strict width AND height match
          if (
            actualWidth !== expectedWidth ||
            actualHeight !== expectedHeight
          ) {
            setImageError(
              `Image must be exactly ${expectedWidth} x ${expectedHeight}px. You uploaded ${actualWidth} x ${actualHeight}px.`,
            );
            return;
          }
        }

        // ✅ Valid image
        setImagePreview(reader.result);
        setImage(selectedImage);
        setImageError("");
      };
      img.onerror = () => {
        setImageError("Failed to load image. Please try another file.");
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(selectedImage);
  };

  const handleSave = () => {
    let error = false;

    if (!isEditMode && !image) {
      setImageError(req("Image"));
      error = true;
    }
    if (!slideType.trim()) {
      setSlideTypeError(req("Type"));
      error = true;
    }
    if (!name.trim()) {
      setNameError(req("Name"));
      error = true;
    }
    if (!view.trim()) {
      setViewError(req("View"));
      error = true;
    }
    if (!title.trim()) {
      setSlideTitleError(req("Slide Title"));
      error = true;
    }
    if (!description.trim()) {
      setSlideDescriptionError(req("Description"));
      error = true;
    }

    if (error) return;

    const formData = new FormData();
    formData.append("name", name.trim());
    formData.append("view", view.trim());
    formData.append("type", slideType.trim());
    formData.append("title", title.trim());
    formData.append("description", description.trim());
    formData.append("status", status ? "active" : "inactive");
    formData.append("shopId", shopId);
    formData.append("productId", productId);
    if (categoryId) formData.append("categoryId", categoryId);
    if (brandId) formData.append("brandId", brandId);
    if (productId) formData.append("productId", productId);
    if (image) {
      formData.append("image", image);
    }

    setSaving(true);

    const url = isEditMode
      ? `/slide_show/?id=${slideIdFromUrl}`
      : "/slide_show/";

    api
      .post(url, formData, superAdminReqConfig())
      .then((response) => {
        if (response.status === 200) {
          toast({
            type: SUCCESS,
            message: isEditMode
              ? "Slide updated successfully!"
              : "Slide added successfully!",
          });
          navigate("/admin/content/contentList");
          if (!isEditMode) clearFields();
        } else {
          toast({ type: ERROR, message: "Something went wrong while saving." });
        }
      })
      .catch((error) => {
        const msg =
          error.response?.data?.message || error.message || "Failed to save.";
        toast({ type: ERROR, message: msg });
      })
      .finally(() => {
        setSaving(false);
      });
  };

  useEffect(() => {
    const id = slideIdFromUrl ? parseInt(slideIdFromUrl, 10) : null;
    if (id) {
      api
        .get(`/slide_show/?id=${id}`, superAdminReqConfig())
        .then((res) => {
          const slideArray = res.data?.data || [];
          const data = slideArray.length > 0 ? slideArray[0] : {};

          setName(data.name || "");
          setView(data.view || "");
          setSlideType(data.type || "");
          setTitle(data.title || "");
          setDescription(data.description || "");
          setCategoryId(data.categoryId || "");
          setBrandId(data.brandId || "");
          setProductId(data.productId || "");
          setStatus(data.status === "active" || data.status === "1");
          if (data.imageUrl) {
            const fullImageUrl = data.imageUrl.startsWith("http")
              ? data.imageUrl
              : `${baseURL}/${data.imageUrl}`;
            setImagePreview(fullImageUrl);
          }
        })
        .catch(() => {
          toast({ type: ERROR, message: "Failed to load slide details." });
        });
    } else {
      clearFields();
    }
  }, [slideIdFromUrl]);

  const inputStyle = {
    borderRadius: "5px",
    fontWeight: "400",
    outline: "none",
    width: "100%",
    padding: "11px 24px",
    backgroundColor: "transparent !important",
    border: "1px solid var(--gray-100)",
    color: "hsl(var(--black))",
    opacity: saving ? 0.7 : 1,
    pointerEvents: saving ? "none" : "auto",
  };

  // ✅ Filter parent categories (parentId !== "-1")
  const parentCategories = categoryList.filter(
    (cat) => String(cat.parentId) !== "-1",
  );

  return (
    <>
      {isViewMode ? (
        <>
          <AdminSideBar
            menuContent={menuContent}
            isMinimized={isSidebarMinimized}
          />
          <AdminTopNavBar
            toggleSidebar={toggleSidebar}
            isSidebarMinimized={isSidebarMinimized}
            homeLink={homeLink}
            title="SLIDE DETAILS"
          />
          <div
            className="content-wrapper p-0"
            style={{
              marginLeft: isSidebarMinimized ? "80px" : "250px",
              flex: "1",
            }}
          >
            <section
              style={{
                backgroundColor: "#F1F4F9",
                padding: "30px",
                marginTop: "70px",
                marginBottom: "40px",
              }}
            >
              <div className="container-fluid">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="card border-0">
                      <div className="d-flex justify-content-between align-items-center p-16">
                        <h6 className="mb-0 fs-5">Slide Information</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6 mb-16">
                            <h6 className="text-md mb-16 fw-bold">
                              Slide Name
                            </h6>
                            <p>{name || "N/A"}</p>
                          </div>
                          <div className="col-md-6 mb-16">
                            <h6 className="text-md mb-16 fw-bold">Platform</h6>
                            <p>
                              {view === "mobile"
                                ? "Mobile App"
                                : view === "web"
                                  ? "Website"
                                  : "N/A"}
                            </p>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6 mb-16">
                            <h6 className="text-md mb-16 fw-bold">
                              Slide Type
                            </h6>
                            <p>{slideType || "N/A"}</p>
                          </div>
                          <div className="col-md-6 mb-16">
                            <h6 className="text-md mb-16 fw-bold">Title</h6>
                            <p>{title || "N/A"}</p>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6 mb-16">
                            <h6 className="text-md mb-16 fw-bold">
                              Description
                            </h6>
                            <p>{description || "N/A"}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card mt-22 pb-40 border-0">
                      <div className="d-flex justify-content-between align-items-center p-16">
                        <h6 className="mb-0 fs-5">Slide Image</h6>
                      </div>
                      <div className="card-body text-center">
                        {imagePreview ? (
                          <img
                            src={imagePreview}
                            alt="Slide Preview"
                            style={{ maxHeight: "300px", maxWidth: "100%" }}
                          />
                        ) : (
                          <p>No image available</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
          <AdminFooter isSidebarMinimized={isSidebarMinimized} />
        </>
      ) : (
        <>
          <AdminSideBar
            menuContent={menuContent}
            isMinimized={isSidebarMinimized}
          />
          <AdminTopNavBar
            toggleSidebar={toggleSidebar}
            isSidebarMinimized={isSidebarMinimized}
            homeLink={homeLink}
            title={isEditMode ? "EDIT SLIDE" : "ADD NEW SLIDES"}
          />
          <div
            className="content-wrapper p-0"
            style={{
              marginLeft: isSidebarMinimized ? "80px" : "250px",
              flex: "1",
            }}
          >
            <section
              style={{
                backgroundColor: "#F1F4F9",
                padding: "30px",
                marginTop: "70px",
                marginBottom: "40px",
              }}
            >
              <div className="container-fluid">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="card border-0">
                      <div className="d-flex justify-content-between align-items-center p-16">
                        <h6 className="mb-0 fs-5">
                          {isEditMode ? "Edit Information" : "Add Information"}
                        </h6>
                        <div className="d-flex align-items-center gap-2">
                          <span className="pe-10">Status</span>
                          <div className="form-check form-switch mb-0">
                            <input
                              className={`form-check-input form-product-status-switch ${
                                status === true
                                  ? "switch-green"
                                  : "switch-orange"
                              }`}
                              type="checkbox"
                              id="statusSwitch"
                              checked={status}
                              disabled={saving}
                              onChange={(e) =>
                                !saving && setStatus(e.target.checked)
                              }
                            />
                          </div>
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6 mb-16">
                            <h6
                              htmlFor="slideName"
                              className="text-md mb-16 fw-bold "
                            >
                              Slide Name
                            </h6>
                            <input
                              type="text"
                              className="form-control"
                              id="slideName"
                              placeholder="Enter Slide Name"
                              value={name}
                              onChange={(e) => {
                                if (saving) return;
                                setName(e.target.value);
                                setNameError("");
                              }}
                              disabled={saving}
                              style={inputStyle}
                            />
                            {nameError && (
                              <span className="text-danger my-2 small fw-bold">
                                {nameError}
                              </span>
                            )}
                          </div>
                          <div className="col-md-6 mb-16">
                            <h6
                              htmlFor="viewSelect"
                              className="text-md mb-16 fw-bold "
                            >
                              Mobile App or Website
                            </h6>
                            <select
                              className="form-select"
                              id="viewSelect"
                              value={view}
                              onChange={(e) => {
                                if (saving) return;
                                setView(e.target.value);
                                setViewError("");
                              }}
                              disabled={saving}
                              style={inputStyle}
                            >
                              <option value="">Select</option>
                              <option value="mobile">Mobile App</option>
                              <option value="web">Website</option>
                            </select>
                            {viewError && (
                              <span className="text-danger my-2 small fw-bold">
                                {viewError}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6 mb-16">
                            <h6
                              htmlFor="slideTypeSelect"
                              className="text-md mb-16 fw-bold "
                            >
                              Slide Type
                            </h6>
                            <select
                              className="form-select"
                              id="slideTypeSelect"
                              value={slideType}
                              onChange={(e) => {
                                if (saving) return;
                                setSlideType(e.target.value);
                                setSlideTypeError("");
                                if (image) {
                                  setImage(null);
                                  setImagePreview("");
                                  setImageError("");
                                }
                              }}
                              disabled={saving}
                              style={inputStyle}
                            >
                              <option value="">Select Type</option>
                              {view === "web" && (
                                <>
                                  <option value="main">Main</option>
                                  <option value="best seller">
                                    Best Seller
                                  </option>
                                  <option value="hot deals">Hot Deals</option>
                                  <option value="day deals">Day Deals</option>
                                  <option value="deals of the day">
                                    Deals Of The day
                                  </option>
                                  <option value="new arrivals">
                                    New Arrivals
                                  </option>
                                  <option value="top selling products">
                                    Top Selling Products
                                  </option>
                                  <option value="trending products">
                                    Trending Products
                                  </option>
                                  <option value="featured products">
                                    Featured Products
                                  </option>
                                  <option value="big deals slide">
                                    Big Deals Slide
                                  </option>
                                  <option value="recommended for you">
                                    Recommended For You
                                  </option>
                                </>
                              )}
                              {view === "mobile" && (
                                <>
                                  <option value="mobile-ad banner1">
                                    Ad Banner1
                                  </option>
                                  <option value="mobile-ad banner2">
                                    Ad Banner2
                                  </option>
                                  <option value="mobile-main">
                                    Mobile - Main
                                  </option>
                                  <option value="mobile-best seller">
                                    Mobile - Best Seller
                                  </option>
                                  <option value="mobile-hot deals">
                                    Mobile - Hot Deals
                                  </option>
                                  <option value="mobile-day deals">
                                    Mobile - Day Deals
                                  </option>
                                  <option value="mobile-new arrivals">
                                    Mobile - New Arrivals
                                  </option>
                                  <option value="thumbnail1">Thumbnail1</option>
                                  <option value="thumbnail2">Thumbnail2</option>
                                  <option value="thumbnail3">Thumbnail3</option>
                                  <option value="thumbnail4">Thumbnail3</option>
                                </>
                              )}

                              <option value="sponsored">Sponsored</option>
                            </select>
                            {slideTypeError && (
                              <span className="text-danger my-2 small fw-bold">
                                {slideTypeError}
                              </span>
                            )}
                          </div>
                          <div className="col-md-6 mb-16">
                            <h6
                              htmlFor="title"
                              className="text-md mb-16 fw-bold "
                            >
                              Slide Title
                            </h6>
                            <input
                              type="text"
                              className="form-control"
                              id="title"
                              placeholder="Enter Title Name"
                              value={title}
                              onChange={(e) => {
                                if (saving) return;
                                setTitle(e.target.value);
                                setSlideTitleError("");
                              }}
                              disabled={saving}
                              style={inputStyle}
                            />
                            {slideTitleError && (
                              <span className="text-danger my-2 small fw-bold">
                                {slideTitleError}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6 mb-16">
                            <h6 className="text-md mb-16 fw-bold">Category</h6>
                            <select
                              className="form-select"
                              value={categoryId}
                              onChange={(e) =>
                                !saving && setCategoryId(e.target.value)
                              }
                              disabled={saving || fetchingCategory}
                              style={inputStyle}
                            >
                              <option value="">Select Category</option>
                              {parentCategories.map((cat) => (
                                <option key={cat.id} value={cat.id}>
                                  {cat.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-6 mb-16">
                            <h6 className="text-md mb-16 fw-bold">Brand</h6>
                            <select
                              className="form-select"
                              value={brandId}
                              onChange={(e) =>
                                !saving && setBrandId(e.target.value)
                              }
                              disabled={saving || fetchingBrand || !categoryId}
                              style={inputStyle}
                            >
                              <option value="">
                                {categoryId
                                  ? "Select Brand"
                                  : "Select Category First"}
                              </option>
                              {brandList.map((brand) => (
                                <option key={brand.id} value={brand.id}>
                                  {brand.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6 mb-16">
                            <h6 className="text-md mb-16 fw-bold">Product</h6>
                            <select
                              className="form-select"
                              value={productId}
                              onChange={(e) =>
                                !saving && setProductId(e.target.value)
                              }
                              disabled={saving || fetchingProduct || !brandId}
                              style={inputStyle}
                            >
                              <option value="">
                                {brandId
                                  ? "Select Product"
                                  : "Select Brand First"}
                              </option>
                              {productList.map((product) => (
                                <option key={product.productId} value={product.productId}>
                                  {product.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-6 mb-16">
                            <h6
                              htmlFor="description"
                              className="text-md mb-16 fw-bold "
                            >
                              Slide Description
                            </h6>
                            <textarea
                              className="form-control"
                              id="description"
                              rows="4"
                              placeholder="Your Description Here..."
                              value={description}
                              onChange={(e) => {
                                if (saving) return;
                                setDescription(e.target.value);
                                setSlideDescriptionError("");
                              }}
                              disabled={saving}
                              style={{
                                ...inputStyle,
                                padding: "10px 24px",
                                minHeight: "100px",
                              }}
                            ></textarea>
                            {slideDescriptionError && (
                              <span className="text-danger my-2 small fw-bold">
                                {slideDescriptionError}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="mb-16"></div>
                      </div>
                    </div>
                    <div className="card mt-22 pb-40 border-0">
                      <div className="d-flex justify-content-between align-items-center p-16">
                        <h6 className="mb-0 fs-5">
                          {isEditMode
                            ? "Replace Slide Image"
                            : "Add Slide Image"}
                        </h6>
                        {isEditMode ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!saving) imageElement.current?.click();
                            }}
                            disabled={saving}
                            style={getButtonStyle(true, true)}
                          >
                            {saving ? (
                              <>
                                {renderLoadingSpinner("sm")}
                                Processing...
                              </>
                            ) : (
                              <>
                                <img
                                  src="/assets/images/upload.png"
                                  alt="Replace"
                                  style={{
                                    width: "24px",
                                    height: "16px",
                                    marginRight: "5px",
                                  }}
                                />
                                Replace
                              </>
                            )}
                          </button>
                        ) : (
                          ""
                        )}
                      </div>
                      <div className="card-body" style={{ height: "350px" }}>
                        <div
                          className="border-dashed text-center cursor-pointer"
                          onClick={() =>
                            !saving && imageElement.current?.click()
                          }
                          style={{
                            border: "3px dashed #ccc",
                            borderRadius: "8px",
                            transition: "border-color 0.2s",
                            minHeight: "270px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            opacity: saving ? 0.6 : 1,
                            cursor: saving ? "not-allowed" : "pointer",
                          }}
                          onMouseEnter={(e) => {
                            if (!saving)
                              e.currentTarget.style.borderColor = "#0d6efd";
                          }}
                          onMouseLeave={(e) => {
                            if (!saving)
                              e.currentTarget.style.borderColor = "#ccc";
                          }}
                        >
                          {saving ? (
                            <>
                              <i
                                className="fa fa-spinner fa-spin mb-3"
                                style={{ fontSize: "48px", color: "#9e9e9e" }}
                              ></i>
                              <h6>Processing...</h6>
                              <small className="text-muted">
                                Please wait while saving...
                              </small>
                            </>
                          ) : imagePreview ? (
                            <img
                              src={imagePreview}
                              alt="Preview"
                              style={{ maxHeight: "120px", maxWidth: "100%" }}
                            />
                          ) : (
                            <div>
                              <i
                                className="fa fa-cloud-upload-alt mb-3"
                                style={{ fontSize: "48px", color: "#0d6efd" }}
                              ></i>
                              <h6>
                                Drop Your Images Here, Or{" "}
                                <span
                                  className="text-primary"
                                  style={{ cursor: "pointer" }}
                                >
                                  Click To Browse
                                </span>
                              </h6>
                              <small className="text-muted">
                                {slideType && imageSizeMap[slideType]
                                  ? `${imageSizeMap[slideType].width} x ${imageSizeMap[slideType].height} Pixels Required`
                                  : "Select slide type first"}
                              </small>
                            </div>
                          )}
                          <input
                            type="file"
                            name="image"
                            id="image"
                            onChange={(e) => {
                              if (!saving) {
                                handleImageChange(e.target.files);
                                e.target.value = "";
                              }
                            }}
                            className="d-none"
                            accept="image/*"
                            ref={imageElement}
                            disabled={saving}
                          />

                          {imageError && (
                            <div className="text-center mt-2">
                              <span className="text-danger my-2 small fw-bold">
                                {imageError}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="d-flex justify-content-end gap-8 mt-22">
                          <button
                            onClick={handleSave}
                            disabled={saving}
                            style={getButtonStyle(true)}
                          >
                            {saving ? (
                              <>
                                {renderLoadingSpinner("sm")}
                                {isEditMode ? "Updating..." : "Adding..."}
                              </>
                            ) : isEditMode ? (
                              "Update"
                            ) : (
                              "Add"
                            )}
                          </button>
                          <button
                            onClick={clearFields}
                            disabled={saving}
                            style={getButtonStyle(false)}
                          >
                            {saving ? (
                              <>
                                {renderLoadingSpinner("sm")}
                                Clearing...
                              </>
                            ) : (
                              "Clear All"
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
          <AdminFooter isSidebarMinimized={isSidebarMinimized} />
        </>
      )}
    </>
  );
};

export default AddSlide;
